import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton, Result } from "antd";
import Swal from "sweetalert2";
import TitleBar from "../../common/title-bar/title-bar.js";
import Button from "../../common/button/button.js";
import TabFade from "../../common/tab-fade";
import { useAuth } from "../AuthProvider.js";
import UserDetailsTab from "./UserDetailsTab";
import UserPermissionsTab from "./UserPermissionsTab";
import { canAdminPerformUserAction } from "./userActionAccess.js";
import {
  getUser,
  getUserActivity,
  updateUser,
  inviteUser,
  resendInvite,
  revokeUser,
  deleteUser,
  getUserStatus,
  getUserStatusType,
  getUserRoleLabel,
  USER_TYPES,
  USER_STATUS,
} from "../../Service/userService";
import "./UserDetails.css";

/**
 * Unified user management page: `/User/:id`.
 *
 * Renders any user type (Viewer / Account / Manufacturer / Distributor). The
 * Role-Specific fields and the header action buttons are shown conditionally
 * per the Figma annotations (see UserDetailsTab + the button block below).
 */
const UserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, logOut } = useAuth();

  const [user, setUser] = useState(null);
  const [values, setValues] = useState({});
  const [activity, setActivity] = useState([]);
  const operationRef = useRef(null);
  const userRequest = useRef(0);
  const currentPageId = useRef(id);
  currentPageId.current = id;
  const saveInFlight = useRef(false);
  const activityRequest = useRef(0);
  const [nextActivityCursor, setNextActivityCursor] = useState(null);
  const [activityError, setActivityError] = useState(false);
  const [activityLoading, setActivityLoading] = useState(true);
  const [pageState, setPageState] = useState("loading");
  const [selectedTab, setSelectedTab] = useState("Details");
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const seedValues = (u) => ({
    Email: u.Email,
    FirstName: u.FirstName,
    LastName: u.LastName,
    Phone: u.Phone,
    Prefix: u.Prefix,
    ContactEmail: u.ContactEmail,
    AccountContactType: u.IsProvider ? "Provider" : u.AccountContactType,
    ProviderNPI: u.ProviderNPI,
    RegionName: u.RegionName,
    AccountName: u.AccountName,
    ManufacturerName: u.ManufacturerName,
    DistributorName: u.DistributorName,
    IsManager: u.IsManager,
  });

  const fetchUser = useCallback(async () => {
    const request = ++userRequest.current;
    setPageState("loading");
    try {
      const data = await getUser(id);
      if (request !== userRequest.current) return;
      if (!data || !data.UserType) {
        setPageState("notFound");
        return;
      }
      setUser(data);
      setValues(seedValues(data));
      setPageState("loaded");
    } catch (err) {
      if (request !== userRequest.current) return;
      console.error("Failed to fetch user", err);
      setPageState(
        err?.response?.status === 404 || err?.response?.status === 400
          ? "notFound"
          : "error",
      );
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
    return () => {
      userRequest.current += 1;
    };
  }, [fetchUser]);

  const refreshActivity = useCallback(
    async (cursor) => {
      const request = ++activityRequest.current;
      setActivityLoading(true);
      setActivityError(false);
      try {
        const items = await getUserActivity(id, cursor);
        if (request !== activityRequest.current) return;
        setActivity((previous) => (cursor ? [...previous, ...items] : items));
        setNextActivityCursor(items.nextCursor ?? null);
      } catch {
        if (request === activityRequest.current) setActivityError(true);
      } finally {
        if (request === activityRequest.current) setActivityLoading(false);
      }
    },
    [id],
  );
  useEffect(() => {
    refreshActivity();
    return () => {
      activityRequest.current += 1;
    };
  }, [refreshActivity]);

  const setValue = (field, val) =>
    setValues((prev) => ({ ...prev, [field]: val }));

  const status = useMemo(() => getUserStatus(user), [user]);
  const canEditUser = canAdminPerformUserAction(authUser, "user.edit");
  const canEditAccountUserType = canAdminPerformUserAction(
    authUser,
    "user.edit",
  );
  const canInviteUser = canAdminPerformUserAction(authUser, "user.invite");
  const canEditEmail =
    canEditUser && canInviteUser && user?.EmailChangeEnabled !== false;
  const canRevokeUser = canAdminPerformUserAction(authUser, "user.revoke");
  const canDeleteUser = canAdminPerformUserAction(authUser, "user.delete");
  const canViewUserPermissions =
    canAdminPerformUserAction(authUser, "user.permissions.view") &&
    Boolean(authUser?.IsSuperAdmin);

  // -------------------------
  // Handlers
  // -------------------------
  const handleEdit = () => {
    if (!canEditUser) return;
    operationRef.current = null;
    setIsEditable(true);
  };

  const handleSave = async () => {
    if (!canEditUser || saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      const isAccountUser = user.UserType === USER_TYPES.ACCOUNT;
      const convertingToProvider =
        isAccountUser &&
        !user.IsProvider &&
        values.AccountContactType === "Provider";

      if (
        isAccountUser &&
        values.AccountContactType === "Provider" &&
        !/^\d{10}$/.test(values.ProviderNPI || "")
      ) {
        await Swal.fire(
          "Provider NPI required",
          "Provider NPI must be exactly 10 digits.",
          "error",
        );
        return;
      }

      if (convertingToProvider) {
        const result = await Swal.fire({
          title: "Convert this user to a provider?",
          text: "They keep their current Tissue Vault access. Provider conversion cannot be reversed from this page.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Convert to Provider",
        });
        if (!result.isConfirmed) return;
      }

      // A provider's contact email is also their login email. With portal
      // access, changing it is a login email change (confirm + new invite);
      // an uninvited provider's login email simply follows the contact email.
      const providerEmail =
        isAccountUser && values.AccountContactType === "Provider";
      const normalizedEmail = String(
        (providerEmail ? values.ContactEmail : values.Email) ??
          user.Email ??
          "",
      )
        .trim()
        .toLowerCase();
      const emailChanged =
        !(providerEmail && status === USER_STATUS.NOT_INVITED) &&
        normalizedEmail !==
          String(user.Email || "")
            .trim()
            .toLowerCase();
      if (emailChanged) {
        if (!canEditEmail) return;
        if (
          normalizedEmail.length > 255 ||
          !/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(normalizedEmail)
        ) {
          await Swal.fire(
            "Invalid email",
            "Enter a valid email address of 255 characters or fewer.",
            "error",
          );
          return;
        }
        const selfChange =
          Number(authUser?.ID ?? authUser?.UserID) === Number(user.UserID);
        // Someone never invited gets no invitation at the new address; the
        // server makes the final call and the save message follows it.
        const result = await Swal.fire(
          status === USER_STATUS.NOT_INVITED
            ? {
                title: "Change email?",
                text: `Change ${user.Email || "no email"} to ${normalizedEmail}? This user has not been invited, so no invitation will be sent. Use Send Invite when they should get Tissue Vault access.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Change email",
              }
            : {
                title: "Change login email?",
                text: `Change ${user.Email || "no email"} to ${normalizedEmail}? Portal access will stop immediately. A new invitation must be accepted and a new password set.${selfChange ? " You will be signed out, including if you are the last superadmin." : ""}`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Change email and invite",
              },
        );
        if (!result.isConfirmed) return;
      }
      const payload = {
        UserID: user.UserID,
        UserType: user.UserType,
        FirstName: values.FirstName,
        LastName: values.LastName,
        Phone: values.Phone,
        RegionName: values.RegionName,
        AccountName: values.AccountName,
        ManufacturerName: values.ManufacturerName,
        DistributorName: values.DistributorName,
        IsManager: values.IsManager,
      };

      if (
        isAccountUser &&
        canEditAccountUserType &&
        values.AccountContactType
      ) {
        payload.AccountContactType = values.AccountContactType;
        if (values.AccountContactType === "Provider") {
          payload.Prefix = values.Prefix;
          payload.ContactEmail = values.ContactEmail;
          payload.ProviderNPI = values.ProviderNPI;
        }
      }

      if (emailChanged) {
        const fingerprint = JSON.stringify([normalizedEmail, payload]);
        if (operationRef.current?.fingerprint !== fingerprint)
          operationRef.current = {
            fingerprint,
            id: window.crypto.randomUUID(),
          };
        Object.assign(payload, {
          Email: normalizedEmail,
          ExpectedEmail: user.Email,
          EmailVersion: user.EmailVersion ?? 0,
          OperationID: operationRef.current.id,
        });
      }
      setIsSaving(true);
      try {
        const response = await updateUser(payload);
        const result = response?.data?.data;
        if (result?.requiresSignIn) {
          await Swal.fire(
            "Email updated",
            "Your invitation is queued. Accept it and set a new password before signing in again.",
            "success",
          );
          await logOut();
          return;
        }
        if (currentPageId.current !== id) return;
        Swal.fire(
          "Saved",
          !emailChanged
            ? "User details have been updated."
            : result?.notificationStatus === "queued"
              ? "Email updated; invitation queued. Portal access remains inactive until acceptance."
              : "Email updated. No invitation was sent.",
          "success",
        );
        setIsEditable(false);
        await Promise.all([fetchUser(), refreshActivity()]);
      } catch (err) {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Failed to update user details.",
          "error",
        );
      } finally {
        setIsSaving(false);
      }
    } finally {
      saveInFlight.current = false;
    }
  };

  const confirmAndRun = async (title, text, action, successMsg, onSuccess) => {
    const result = await Swal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2663EB",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });
    if (!result.isConfirmed) return;
    try {
      await action(user);
      Swal.fire("Done", successMsg, "success").then(() =>
        onSuccess ? onSuccess() : Promise.all([fetchUser(), refreshActivity()]),
      );
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Something went wrong.",
        "error",
      );
    }
  };

  const handleSendInvite = () => {
    if (!canInviteUser) return;
    return confirmAndRun(
      "Send invite?",
      `Send an invite to ${user.Email || "this user"}?`,
      inviteUser,
      "Invitation queued.",
    );
  };

  const handleResendInvite = () => {
    if (!canInviteUser) return;
    return confirmAndRun(
      "Resend invite?",
      `Resend the invite to ${user.Email || "this user"}?`,
      resendInvite,
      "Invitation queued.",
    );
  };

  const handleRevoke = () => {
    if (!canRevokeUser) return;
    return confirmAndRun(
      "Revoke access?",
      "This user will lose access to Tissue Vault.",
      revokeUser,
      "Access revoked.",
    );
  };

  const handleDelete = () => {
    if (!canDeleteUser) return;
    const fullName = `${user.FirstName || ""} ${user.LastName || ""}`.trim();
    return confirmAndRun(
      "Delete user?",
      `${fullName || "This user"} will be removed from Tissue Vault and will no longer appear anywhere in the portal. This cannot be undone.`,
      deleteUser,
      "User deleted.",
      () => navigate("/Users"),
    );
  };

  // -------------------------
  // Render states
  // -------------------------
  if (pageState === "loading") {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (pageState === "notFound") {
    return (
      <Result
        status="404"
        title="User Does Not Exist"
        subTitle={`No accessible user exists for ID ${id || "(missing)"}.`}
        extra={
          <Button onClick={() => navigate("/Users")}>Back to Users</Button>
        }
      />
    );
  }
  if (pageState === "error") {
    return (
      <Result
        status="error"
        title="Unable to Load User"
        subTitle="An unexpected error occurred while loading this user."
        extra={<Button onClick={fetchUser}>Retry</Button>}
      />
    );
  }

  const fullName =
    `${user.FirstName || ""} ${user.LastName || ""}`.trim() || "User";
  const displayStatus = status;

  // Action buttons — visibility follows the Figma annotations.
  const actionButtons = (
    <div className="user-details-actions">
      {canEditUser && isEditable ? (
        <Button
          variant="table"
          subVariant="success"
          style={{ minWidth: "100px" }}
          disabled={isSaving}
          onClick={handleSave}
        >
          Save
        </Button>
      ) : canEditUser ? (
        <Button
          variant="table"
          subVariant="warning"
          style={{ minWidth: "100px" }}
          onClick={handleEdit}
        >
          Edit
        </Button>
      ) : null}
      {status === USER_STATUS.NOT_INVITED && canInviteUser && (
        <Button
          variant="table"
          subVariant="primary"
          style={{ minWidth: "100px" }}
          onClick={handleSendInvite}
        >
          Send Invite
        </Button>
      )}
      {status === USER_STATUS.INVITE_SENT && canInviteUser && (
        <Button
          variant="table"
          subVariant="primary"
          style={{ minWidth: "100px" }}
          onClick={handleResendInvite}
        >
          Resend Invite
        </Button>
      )}
      {status === USER_STATUS.ACTIVE && canRevokeUser && (
        <Button
          variant="table"
          subVariant="failure"
          style={{ minWidth: "100px" }}
          onClick={handleRevoke}
        >
          Revoke Access
        </Button>
      )}
      {status === USER_STATUS.REVOKED && canInviteUser && (
        <Button
          variant="table"
          subVariant="primary"
          style={{ minWidth: "100px" }}
          onClick={handleSendInvite}
        >
          Send Invite
        </Button>
      )}
      {/* Delete is offered for every user type and status, providers
            included — for them it removes the contact rather than access. */}
      {canDeleteUser && (
        <Button
          variant="table"
          subVariant="failure"
          style={{ minWidth: "100px" }}
          onClick={handleDelete}
        >
          Delete
        </Button>
      )}
    </div>
  );

  return (
    <div>
      <TitleBar
        entityName="Users"
        entityNameLink="/Users"
        label={fullName}
        invoiceNumber={getUserRoleLabel(user)}
        statusIndicatorStatus={displayStatus}
        statusIndicatorStatusType={getUserStatusType(displayStatus)}
        filterBarComponents={
          <>
            <Button
              variant="timeframe"
              isActive={selectedTab === "Details"}
              onClick={() => setSelectedTab("Details")}
            >
              Details
            </Button>
            {canViewUserPermissions && (
              <Button
                variant="timeframe"
                isActive={selectedTab === "Permissions"}
                onClick={() => setSelectedTab("Permissions")}
              >
                Permissions
              </Button>
            )}
          </>
        }
        rightFilterBarComponents={
          selectedTab === "Details" ? actionButtons : null
        }
      />

      {user.NotificationStatus && (
        <p role="status">
          Latest invitation: {user.NotificationStatus}
          {user.NotificationError
            ? ". Use Resend Invite to create a fresh invitation, or wait for scheduled delivery retries."
            : ""}
        </p>
      )}
      <TabFade activeKey={selectedTab} style={{ marginTop: "20px" }}>
        {selectedTab === "Details" && (
          <UserDetailsTab
            user={user}
            values={values}
            setValue={setValue}
            isEditable={isEditable}
            canEditAccountUserType={canEditAccountUserType}
            canEditEmail={canEditEmail}
            hasPortalAccess={status !== USER_STATUS.NOT_INVITED}
            nextActivityCursor={nextActivityCursor}
            onLoadMoreActivity={() => refreshActivity(nextActivityCursor)}
            onRefreshActivity={() => refreshActivity()}
            activityError={activityError}
            activity={activity}
            activityLoading={activityLoading}
          />
        )}
        {selectedTab === "Permissions" && canViewUserPermissions && (
          <UserPermissionsTab userId={user.UserID} userType={user.UserType} />
        )}
      </TabFade>
    </div>
  );
};

export default UserDetailsPage;
